import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Save, Plus, Search, Tag, X, Upload, Image, Dumbbell, ChevronDown, Shield, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import './EquipmentProfile.css';
import devLog from '../utils/devLog';


// Hover Preview Component
function HoverPreview({ equipment, position }) {
  if (!equipment || !position) return null;
  
  const top = Math.min(position.y - 80, window.innerHeight - 320);
  const left = Math.min(position.x + 20, window.innerWidth - 240);
  
  return (
    <div 
      className="equipment-hover-preview"
      style={{ left: left, top: top }}
    >
      {equipment.imageUrl ? (
        <img src={equipment.imageUrl} alt={equipment.name} />
      ) : (
        <div className="preview-no-image">
          <Image size={40} />
          <span>No image</span>
        </div>
      )}
      <div className="preview-info">
        <div className="preview-name">{equipment.name}</div>
        <div className="preview-abbrev">{equipment.abbrev || 'No abbreviation'}</div>
      </div>
    </div>
  );
}

// Equipment Row Component - Updated to handle viewMode
function EquipmentRow({ 
  item, 
  isSelected, 
  quantity, 
  isHovered, 
  viewMode,
  onToggle, 
  onUpdateQty, 
  onHover, 
  onLeave,
  onEdit 
}) {
  const [showQtyPopover, setShowQtyPopover] = useState(false);
  const [isHoveringCheckbox, setIsHoveringCheckbox] = useState(false);
  const popoverRef = useRef(null);
  const badgeRef = useRef(null);

  const isAdminMode = viewMode === 'admin';

  // Click outside to close popover
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showQtyPopover && 
        popoverRef.current && 
        !popoverRef.current.contains(event.target) &&
        badgeRef.current &&
        !badgeRef.current.contains(event.target)
      ) {
        setShowQtyPopover(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showQtyPopover]);

  const handleMouseMove = (e) => {
    onHover(item, { x: e.clientX, y: e.clientY });
  };

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    if (!isAdminMode) {
      onToggle(item.id);
    }
    setShowQtyPopover(false);
  };

  const handleBadgeClick = (e) => {
    e.stopPropagation();
    setShowQtyPopover(!showQtyPopover);
  };

  return (
    <div
      className={`equipment-row ${isSelected && !isAdminMode ? 'selected' : ''} ${isHovered ? 'hovered' : ''}`}
      onMouseEnter={handleMouseMove}
      onMouseMove={handleMouseMove}
      onMouseLeave={onLeave}
    >
      {/* Thumbnail with Checkbox - only show checkbox in User mode */}
      <div 
        className="equipment-thumb"
        style={item.imageUrl ? { backgroundImage: `url(${item.imageUrl})` } : {}}
      >
        {!item.imageUrl && <Image size={14} />}
        
        {/* Checkbox on thumbnail - only in User mode */}
        {!isAdminMode && (
          <div 
            className={`equipment-thumb-checkbox ${isSelected ? 'checked' : ''} ${isHoveringCheckbox ? 'hover' : ''}`}
            onClick={handleCheckboxClick}
            onMouseEnter={() => setIsHoveringCheckbox(true)}
            onMouseLeave={() => setIsHoveringCheckbox(false)}
          >
            {isSelected && <span>✓</span>}
          </div>
        )}
      </div>

      {/* Name + Abbreviation + Edit */}
      <div className="equipment-info">
        <div className="equipment-name">{item.name}</div>
        <div className="equipment-abbrev-row">
          <span className="equipment-abbrev">{item.abbrev}</span>
          {/* Edit button - only in Admin mode on hover */}
          {isAdminMode && isHovered && (
            <button 
              className="equipment-edit-btn"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(item);
              }}
            >
              Edit
            </button>
          )}
        </div>
      </div>

      {/* Quantity Badge - only in Admin mode */}
      {isAdminMode ? (
        <div className="equipment-qty-badge-container">
          <div 
            ref={badgeRef}
            className={`equipment-qty-badge ${showQtyPopover ? 'active' : ''}`}
            onClick={handleBadgeClick}
          >
            Qty: {quantity || 1}
            <ChevronDown 
              size={14} 
              className={`qty-badge-arrow ${showQtyPopover ? 'rotated' : ''}`}
            />
          </div>
          
          {/* Quantity Popover */}
          {showQtyPopover && (
            <div 
              ref={popoverRef}
              className="equipment-qty-popover"
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                className="qty-btn"
                onClick={() => onUpdateQty(item.id, -1)}
                disabled={(quantity || 1) <= 1}
              >
                −
              </button>
              <span className="qty-value">{quantity || 1}</span>
              <button 
                className="qty-btn plus"
                onClick={() => onUpdateQty(item.id, 1)}
              >
                +
              </button>
            </div>
          )}
        </div>
      ) : (
        // In User mode, show nothing in this space (or placeholder for alignment)
        <div className="equipment-qty-placeholder" />
      )}
    </div>
  );
}

// Add/Edit Equipment Modal
function EquipmentModal({ equipment, categories: initialCategories, onClose, onSave, onDelete, onCategoryAdded }) {
  const [name, setName] = useState(equipment?.name || '');
  const [abbrev, setAbbrev] = useState(equipment?.abbrev || '');
  const [selectedCategory, setSelectedCategory] = useState(equipment?.categoryId || '');
  const [categorySearch, setCategorySearch] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(equipment?.imageUrl || '');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pendingCategory, setPendingCategory] = useState(null); // New category not yet saved to Firestore
  const dropdownRef = useRef(null);
  const isEditing = !!equipment;

  // Dropdown only shows saved categories (NOT the pending one)
  const filteredCategories = initialCategories.filter(cat =>
    cat.name.toLowerCase().includes(categorySearch.toLowerCase())
  );

  // Check if we should show "Add new" option - exclude pending category name too
  const showAddNewCategory = categorySearch.trim() && 
    !initialCategories.some(cat => cat.name.toLowerCase() === categorySearch.trim().toLowerCase()) &&
    !(pendingCategory && pendingCategory.name.toLowerCase() === categorySearch.trim().toLowerCase());

  // Selected category can be from saved categories OR the pending one (for pill display)
  const selectedCategoryObj = pendingCategory && selectedCategory === pendingCategory.id
    ? pendingCategory
    : initialCategories.find(c => c.id === selectedCategory);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setImageFile(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImageUrl('');
  };

  const handleSave = async () => {
    if (!name.trim() || !selectedCategory) return;
    
    setSaving(true);
    try {
      let finalCategoryId = selectedCategory;
      
      // If there's a pending category, save it to Firestore first
      if (pendingCategory && selectedCategory === pendingCategory.id) {
        const { id, isPending, ...categoryData } = pendingCategory;
        const docRef = await addDoc(collection(db, 'equipmentCategories'), categoryData);
        finalCategoryId = docRef.id;
        
        // Notify parent about the new category
        if (onCategoryAdded) {
          onCategoryAdded({ id: docRef.id, ...categoryData });
        }
      }
      
      let finalImageUrl = imageUrl;
      
      // Upload new image if selected
      if (imageFile) {
        const timestamp = Date.now();
        const safeName = imageFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const fileName = `equipment/${timestamp}_${safeName}`;
        const functions = getFunctions();
        const getUploadUrl = httpsCallable(functions, 'getUploadUrl');
        const result = await getUploadUrl({ fileName, contentType: imageFile.type });
        await fetch(result.data.uploadUrl, {
          method: 'PUT',
          body: imageFile,
          headers: { 'Content-Type': imageFile.type }
        });
        finalImageUrl = result.data.fileUrl;
      }

      await onSave({
        id: equipment?.id,
        name: name.trim(),
        abbrev: abbrev.trim() || null,
        categoryId: finalCategoryId,
        imageUrl: finalImageUrl || null
      });
      
      onClose();
    } catch (error) {
      console.error('Error saving equipment:', error);
      alert('Error saving equipment: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!equipment?.id) return;
    if (!window.confirm(`Are you sure you want to delete "${equipment.name}"? This cannot be undone.`)) return;
    
    setDeleting(true);
    try {
      await onDelete(equipment.id);
      onClose();
    } catch (error) {
      console.error('Error deleting equipment:', error);
      alert('Error deleting equipment: ' + error.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleAddCategory = (categoryName) => {
    // Create a pending category with a temporary ID (not saved to Firestore yet)
    const tempId = `pending_${Date.now()}`;
    const newCategory = {
      id: tempId,
      name: categoryName.trim(),
      color: '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0'),
      createdAt: Date.now(),
      isPending: true // Mark as pending
    };
    
    setPendingCategory(newCategory);
    setSelectedCategory(tempId);
    setCategorySearch('');
    setShowCategoryDropdown(false);
  };

  return (
    <div className="modal-overlay">
      <div className="equipment-modal">
        <div className="modal-header">
          <h3>{isEditing ? 'Edit Equipment' : 'Add New Equipment'}</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {/* Image Upload */}
          <div className="form-group">
            <label>Equipment Image</label>
            <input 
              type="file" 
              accept="image/*" 
              onChange={handleImageSelect} 
              style={{ display: 'none' }} 
              id="equipment-image-input" 
            />
            <label 
              htmlFor="equipment-image-input" 
              className="image-upload-area"
              style={
                (imageFile || imageUrl) 
                  ? { backgroundImage: `url(${imageFile ? URL.createObjectURL(imageFile) : imageUrl})` } 
                  : {}
              }
            >
              {!imageFile && !imageUrl && (
                <>
                  <Upload size={28} />
                  <span>Click to upload</span>
                </>
              )}
            </label>
            {(imageFile || imageUrl) && (
              <button className="remove-image-btn" onClick={handleRemoveImage}>
                Remove image
              </button>
            )}
          </div>

          {/* Equipment Name */}
          <div className="form-group">
            <label>Equipment Name <span className="required">*</span></label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Kettlebells"
              className="form-input"
            />
          </div>

          {/* Abbreviation */}
          <div className="form-group">
            <label>Abbreviation</label>
            <input
              type="text"
              value={abbrev}
              onChange={(e) => setAbbrev(e.target.value)}
              placeholder="e.g., KB"
              className="form-input"
            />
          </div>

          {/* Category */}
          <div className="form-group category-field" ref={dropdownRef}>
            <label>Category <span className="required">*</span></label>
            
            <div 
              className={`category-input-container ${showCategoryDropdown ? 'focused' : ''}`}
              onClick={() => setShowCategoryDropdown(true)}
            >
              {/* Selected Category Pill */}
              {selectedCategoryObj && (
                <div 
                  className="category-pill"
                  style={{ background: selectedCategoryObj.color + '20', color: selectedCategoryObj.color }}
                >
                  <div 
                    className="category-dot" 
                    style={{ background: selectedCategoryObj.color }} 
                  />
                  {selectedCategoryObj.name}
                  <button 
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      setSelectedCategory(''); 
                    }}
                  >
                    ×
                  </button>
                </div>
              )}
              
              {/* Search Input (only show if no category selected) */}
              {!selectedCategoryObj && (
                <input
                  type="text"
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  onFocus={() => setShowCategoryDropdown(true)}
                  placeholder="Search or add category..."
                />
              )}
              
              {/* Clear/dropdown indicator */}
              {selectedCategoryObj ? (
                <span className="dropdown-arrow">▼</span>
              ) : null}
            </div>

            {showCategoryDropdown && (
              <div className="category-dropdown">
                {/* Search within dropdown when category is selected */}
                {selectedCategoryObj && (
                  <div className="dropdown-search">
                    <input
                      type="text"
                      value={categorySearch}
                      onChange={(e) => setCategorySearch(e.target.value)}
                      placeholder="Search categories..."
                      autoFocus
                    />
                  </div>
                )}
                
                <div className="dropdown-header">Categories</div>
                {filteredCategories.map(cat => (
                  <div
                    key={cat.id}
                    className={`category-option ${selectedCategory === cat.id ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedCategory(cat.id);
                      setCategorySearch('');
                      setShowCategoryDropdown(false);
                    }}
                  >
                    <div className="category-dot" style={{ background: cat.color }} />
                    <span>{cat.name}</span>
                    {selectedCategory === cat.id && <span className="check">✓</span>}
                  </div>
                ))}

                {filteredCategories.length === 0 && !showAddNewCategory && (
                  <div className="dropdown-empty">No categories found</div>
                )}

                {showAddNewCategory && (
                  <div
                    className="category-option add-new"
                    onClick={() => handleAddCategory(categorySearch.trim())}
                  >
                    <div className="add-icon">+</div>
                    <div>
                      <div className="add-title">Add "{categorySearch.trim()}"</div>
                      <div className="add-subtitle">Create new category</div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          {isEditing && (
            <button 
              className="btn btn-danger" 
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          )}
          <div className="modal-footer-right">
            <button className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handleSave}
              disabled={!name.trim() || !selectedCategory || saving}
            >
              {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Equipment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main Component
function EquipmentProfile() {
  const [equipmentList, setEquipmentList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // View Mode state - persisted to localStorage
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('equipmentProfileViewMode') || 'user';
  });
  
  // Track unsaved changes
  const [savedEquipment, setSavedEquipment] = useState({});
  const [showSaveToast, setShowSaveToast] = useState(false);
  
  const hasUnsavedChanges = JSON.stringify(selectedEquipment) !== JSON.stringify(savedEquipment);
  
  // Search state
  const [searchMode, setSearchMode] = useState('name'); // 'name' or 'tag'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  // Hover state
  const [hoveredItem, setHoveredItem] = useState(null);
  const [hoverPosition, setHoverPosition] = useState(null);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  
 const { currentUser, isAdmin } = useAuth();
const accountId = isAdmin ? 'hit10fitness' : currentUser?.uid;

  // Force non-admins to user mode (prevents localStorage bleed from admin sessions)
  useEffect(() => {
    if (isAdmin === false) {
      setViewMode('user');
    }
  }, [isAdmin]);

  // Persist viewMode to localStorage
  useEffect(() => {
    localStorage.setItem('equipmentProfileViewMode', viewMode);
  }, [viewMode]);

  // Load data on mount only
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadData = async () => {
    try {
      // Load equipment
      const equipmentSnapshot = await getDocs(collection(db, 'equipment'));
      const equipment = equipmentSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      equipment.sort((a, b) => a.name.localeCompare(b.name));
      setEquipmentList(equipment);

      // Load categories
      const categoriesSnapshot = await getDocs(collection(db, 'equipmentCategories'));
      let cats = categoriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Seed default categories if none exist
      if (cats.length === 0) {
        cats = await seedDefaultCategories();
      }
      
      cats.sort((a, b) => a.name.localeCompare(b.name));
      setCategories(cats);

      // Load equipment profile (what the gym has selected)
      const profileDoc = await getDoc(doc(db, 'equipmentProfiles', accountId));
      if (profileDoc.exists()) {
        const rawEquipment = profileDoc.data().equipment || {};
        
        // Clean up any invalid keys (undefined, null, empty strings, non-Firestore IDs)
        const equipment = {};
        for (const [key, value] of Object.entries(rawEquipment)) {
          // Only keep valid Firestore document IDs (20 alphanumeric chars)
          if (key && key !== 'undefined' && key !== 'null' && /^[a-zA-Z0-9]{20}$/.test(key)) {
            equipment[key] = value;
          } else {
            console.warn('Removing invalid equipment key:', key);
          }
        }
        
        setSelectedEquipment(equipment);
        setSavedEquipment(equipment);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const seedDefaultCategories = async () => {
    const defaultCategories = [
      { name: 'Free Weights', color: '#3b82f6' },
      { name: 'Machines', color: '#8b5cf6' },
      { name: 'Cardio', color: '#ef4444' },
      { name: 'Bodyweight / Accessories', color: '#10b981' },
      { name: 'Mobility / Recovery', color: '#f59e0b' },
    ];

    const seededCats = [];
    for (const cat of defaultCategories) {
      const docRef = await addDoc(collection(db, 'equipmentCategories'), {
        ...cat,
        createdAt: Date.now()
      });
      seededCats.push({ id: docRef.id, ...cat });
    }
    return seededCats;
  };

  const toggleEquipment = (equipmentId) => {
    // Guard against undefined/null IDs
    if (!equipmentId) {
      console.warn('toggleEquipment called with invalid ID:', equipmentId);
      return;
    }
    
    setSelectedEquipment(prev => {
      if (prev[equipmentId]) {
        const { [equipmentId]: removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [equipmentId]: { quantity: 1 } };
    });
  };

  const updateQuantity = (equipmentId, delta) => {
    // Guard against undefined/null IDs
    if (!equipmentId) {
      console.warn('updateQuantity called with invalid ID:', equipmentId);
      return;
    }
    
    // In admin mode, we're updating the master quantity, not user selection
    // For now, we'll store it in a separate state or handle differently
    // This updates the equipment's default quantity
    setEquipmentList(prev => prev.map(eq => {
      if (eq.id === equipmentId) {
        return { ...eq, defaultQuantity: Math.max(1, (eq.defaultQuantity || 1) + delta) };
      }
      return eq;
    }));
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    
    try {
      await setDoc(doc(db, 'equipmentProfiles', accountId), {
        equipment: selectedEquipment,
        updatedAt: Date.now()
      });
      setSavedEquipment(selectedEquipment);
      setShowSaveToast(true);
      setTimeout(() => setShowSaveToast(false), 3000);
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Error saving profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEquipment = async (equipmentData) => {
    if (equipmentData.id) {
      // Get the OLD name before updating (for syncing video tags)
      const oldEquipment = equipmentList.find(eq => eq.id === equipmentData.id);
      const oldName = oldEquipment?.name;
      const newName = equipmentData.name;
      
      // Update existing equipment
      await updateDoc(doc(db, 'equipment', equipmentData.id), {
        name: equipmentData.name,
        abbrev: equipmentData.abbrev,
        categoryId: equipmentData.categoryId,
        imageUrl: equipmentData.imageUrl
      });
      
      // If name changed, update all videos that have this equipment tag
      if (oldName && oldName !== newName) {
        devLog(`Equipment renamed: "${oldName}" → "${newName}". Syncing videos...`);
        
        try {
          const assetsSnapshot = await getDocs(collection(db, 'assets'));
          const updatePromises = [];
          
          assetsSnapshot.docs.forEach(assetDoc => {
            const assetData = assetDoc.data();
            const equipmentTags = assetData.equipmentTags || [];
            
            // Check if this video has the old equipment name
            if (equipmentTags.includes(oldName)) {
              // Replace old name with new name
              const updatedTags = equipmentTags.map(tag => 
                tag === oldName ? newName : tag
              );
              
              devLog(`  Updating video: ${assetData.name}`);
              updatePromises.push(
                updateDoc(doc(db, 'assets', assetDoc.id), {
                  equipmentTags: updatedTags
                })
              );
            }
          });
          
          if (updatePromises.length > 0) {
            await Promise.all(updatePromises);
            devLog(`  ✓ Updated ${updatePromises.length} video(s)`);
          } else {
            devLog(`  No videos found with tag "${oldName}"`);
          }
        } catch (error) {
          console.error('Error syncing video tags:', error);
          // Don't throw - equipment was saved successfully, just log the sync error
        }
      }
      
      setEquipmentList(prev => 
        prev.map(eq => eq.id === equipmentData.id ? { ...eq, ...equipmentData } : eq)
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    } else {
      // Add new
      const docRef = await addDoc(collection(db, 'equipment'), {
        name: equipmentData.name,
        abbrev: equipmentData.abbrev,
        categoryId: equipmentData.categoryId,
        imageUrl: equipmentData.imageUrl,
        createdAt: Date.now()
      });
      
      const newId = docRef.id;
      
      // Add to equipment list (spread equipmentData first, then override id with newId)
      setEquipmentList(prev => 
        [...prev, { ...equipmentData, id: newId }]
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    }
    // Refresh categories in case new one was added
    const categoriesSnapshot = await getDocs(collection(db, 'equipmentCategories'));
    const cats = categoriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    cats.sort((a, b) => a.name.localeCompare(b.name));
    setCategories(cats);
  };

  const handleDeleteEquipment = async (equipmentId) => {
    await deleteDoc(doc(db, 'equipment', equipmentId));
    setEquipmentList(prev => prev.filter(eq => eq.id !== equipmentId));
    // Also remove from selected if present
    setSelectedEquipment(prev => {
      const { [equipmentId]: removed, ...rest } = prev;
      return rest;
    });
  };

  const handleHover = (item, position) => {
    setHoveredItem(item);
    setHoverPosition(position);
  };

  const handleLeave = () => {
    setHoveredItem(null);
    setHoverPosition(null);
  };

  const handleTagSelect = (category) => {
    setSelectedTags(prev => [...prev, category]);
  };

  const handleTagRemove = (categoryId) => {
    setSelectedTags(prev => prev.filter(t => t.id !== categoryId));
  };

  const handleDeleteCategory = async (categoryId, categoryName, equipmentCount) => {
    if (equipmentCount > 0) {
      if (!window.confirm(`"${categoryName}" is used by ${equipmentCount} equipment item(s). Delete anyway? The equipment will become uncategorized.`)) {
        return;
      }
    } else {
      if (!window.confirm(`Delete category "${categoryName}"?`)) {
        return;
      }
    }
    
    try {
      await deleteDoc(doc(db, 'equipmentCategories', categoryId));
      setCategories(prev => prev.filter(c => c.id !== categoryId));
      // Remove from selected tags if it was selected
      setSelectedTags(prev => prev.filter(t => t.id !== categoryId));
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Error deleting category: ' + error.message);
    }
  };

  const handleEdit = (equipment) => {
    setEditingEquipment(equipment);
    setShowModal(true);
  };

  const handleAddNew = () => {
    setEditingEquipment(null);
    setShowModal(true);
  };

  // Filter equipment based on mode
  const filteredEquipment = equipmentList.filter(item => {
    if (searchMode === 'name') {
      if (!searchQuery) return true;
      return item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.abbrev && item.abbrev.toLowerCase().includes(searchQuery.toLowerCase()));
    } else {
      if (selectedTags.length === 0) return true;
      return selectedTags.some(tag => tag.id === item.categoryId);
    }
  });

  // Split into 3 columns
  const columns = [[], [], []];
  filteredEquipment.forEach((item, index) => {
    columns[index % 3].push(item);
  });

  // Filter categories for dropdown
  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getFilterDescription = () => {
    if (searchMode === 'name' && searchQuery) {
      return `matching "${searchQuery}"`;
    } else if (searchMode === 'tag' && selectedTags.length > 0) {
      return `filtered by ${selectedTags.length} tag${selectedTags.length > 1 ? 's' : ''}`;
    }
    return '';
  };

  if (loading) {
    return (
      <div className="equipment-profile">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="equipment-profile">
      {/* Unified Toolbar Styles */}
      <style>{`
        .unified-toolbar {
          display: flex;
          align-items: center;
          background: white;
          border-radius: 12px;
          padding: 6px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          gap: 6px;
        }
        
        .search-section {
          display: flex;
          align-items: center;
          flex: 1;
          position: relative;
        }
        
        .search-mode-toggle {
          display: flex;
          background: #f3f4f6;
          border-radius: 8px;
          padding: 2px;
          margin-right: 8px;
          flex-shrink: 0;
        }
        
        .search-mode-toggle button {
          padding: 8px 10px;
          border: none;
          border-radius: 6px;
          background: transparent;
          color: #666;
          cursor: pointer;
          display: flex;
          align-items: center;
          transition: all 0.15s ease;
        }
        
        .search-mode-toggle button:hover:not(.active) {
          background: #e5e7eb;
        }
        
        .search-mode-toggle button.active {
          background: #E63946;
          color: white;
        }
        
        .unified-search-input-wrapper {
          flex: 1;
          position: relative;
          display: flex;
          align-items: center;
        }
        
        .unified-search-input {
          width: 100%;
          padding: 10px 32px 10px 12px;
          border: none;
          font-size: 16px;
          outline: none;
          background: transparent;
        }
        
        .search-clear-btn {
          position: absolute;
          right: 8px;
          background: none;
          border: none;
          cursor: pointer;
          color: #999;
          padding: 4px;
          display: flex;
          align-items: center;
        }
        
        .search-clear-btn:hover {
          color: #666;
        }
        
        .unified-tag-input {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          cursor: text;
          overflow: hidden;
        }
        
        .selected-tag-pill {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          flex-shrink: 0;
        }
        
        .selected-tag-pill button {
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          display: flex;
          align-items: center;
          opacity: 0.7;
        }
        
        .selected-tag-pill button:hover {
          opacity: 1;
        }
        
        .tag-filter-input {
          flex: 1;
          min-width: 80px;
          border: none;
          outline: none;
          font-size: 16px;
          padding: 4px 0;
          background: transparent;
        }
        
        .clear-all-tags {
          background: none;
          border: none;
          cursor: pointer;
          color: #999;
          padding: 4px;
          display: flex;
          align-items: center;
          flex-shrink: 0;
        }
        
        .clear-all-tags:hover {
          color: #666;
        }
        
        .tag-suggestions-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          margin-top: 8px;
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          max-height: 300px;
          overflow-y: auto;
          z-index: 1000;
        }
        
        .dropdown-section-header {
          padding: 8px 12px;
          font-size: 11px;
          font-weight: 600;
          color: #666;
          text-transform: uppercase;
          border-bottom: 1px solid #eee;
          background: #fafafa;
        }
        
        .tag-suggestion-item {
          display: flex;
          align-items: center;
        }
        
        .tag-suggestion-content {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          cursor: pointer;
        }
        
        .tag-suggestion-content:hover {
          background: #f5f5f5;
        }
        
        .tag-suggestion-item.selected .tag-suggestion-content {
          background: #f0f7ff;
        }
        
        .category-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        
        .category-name {
          font-size: 14px;
          color: #333;
        }
        
        .category-count {
          font-size: 12px;
          color: #999;
        }
        
        .check-mark {
          margin-left: auto;
          color: #2196F3;
          font-weight: 600;
        }
        
        .category-delete-btn {
          background: none;
          border: none;
          cursor: pointer;
          color: #ccc;
          padding: 8px 12px;
          display: flex;
          align-items: center;
        }
        
        .category-delete-btn:hover {
          color: #E63946;
        }
        
        .dropdown-empty-state {
          padding: 16px;
          text-align: center;
          color: #999;
          font-size: 14px;
        }
        
        .toolbar-divider {
          width: 1px;
          height: 28px;
          background: #e5e7eb;
          flex-shrink: 0;
        }
        
        .add-equipment-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          border: none;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          background: #10b981;
          color: white;
          flex-shrink: 0;
          transition: background 0.15s ease;
        }
        
        .add-equipment-btn:hover {
          background: #059669;
        }
        
        @media (max-width: 768px) {
          .unified-toolbar {
            flex-direction: column;
            padding: 8px;
            gap: 8px;
          }
          
          .search-section {
            width: 100%;
          }
          
          .toolbar-divider {
            display: none;
          }
          
          .add-equipment-btn {
            display: none;
          }
          
          .add-equipment-btn span {
            display: none;
          }
        }
      `}</style>

      {/* Mobile View-Only Banner */}
      <div className="mobile-view-only-banner">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        View only — edit on desktop
      </div>

      {/* Save Toast */}
      {showSaveToast && (
        <div className="save-toast">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          Equipment profile saved!
        </div>
      )}

      {/* Hover Preview */}
      <HoverPreview equipment={hoveredItem} position={hoverPosition} />

      {/* Modal */}
      {showModal && (
        <EquipmentModal
          equipment={editingEquipment}
          categories={categories}
          onClose={() => {
            setShowModal(false);
            setEditingEquipment(null);
          }}
          onSave={handleSaveEquipment}
          onDelete={handleDeleteEquipment}
          onCategoryAdded={(newCat) => {
            setCategories(prev => [...prev, newCat].sort((a, b) => a.name.localeCompare(b.name)));
          }}
        />
      )}

      {/* Page Header */}
      <div className="page-header">
        <h1 className="page-title"  style={{fontSize: '20px', fontWeight: 500 }}>Equipment Profile</h1>
        
        <div className="page-header-right">
          {/* Admin/User Toggle */}
          {isAdmin && (
            <div className="view-mode-toggle">
              <button
                className={`toggle-btn ${viewMode === 'admin' ? 'active admin' : ''}`}
                onClick={() => setViewMode('admin')}
                title="Admin View"
              >
                <Shield size={16} />
                <span>Admin</span>
              </button>
              <button
                className={`toggle-btn ${viewMode === 'user' ? 'active user' : ''}`}
                onClick={() => setViewMode('user')}
                title="User View"
              >
                <User size={16} />
                <span>User</span>
              </button>
            </div>
          )}
          
          {/* Save Button - only in User mode */}
          {viewMode === 'user' && (
            <button
  className="btn btn-danger"
  onClick={handleSaveProfile}
  disabled={saving}
  style={{ padding: '9px 18px', fontSize: '13px', fontWeight: 500 }}
>
  <Save size={16} />
  {saving ? 'Saving...' : 'Save'}
</button>
          )}
        </div>
      </div>

      {/* Intro Card */}
      <div className="equipment-intro">
        <Dumbbell size={24} />
        <div>
          <h3>{viewMode === 'admin' ? 'Manage Equipment Catalog' : 'Select Your Gym Equipment'}</h3>
          <p>
            {viewMode === 'admin' 
              ? 'Add, edit, and manage the equipment available in the catalog. Set quantities and categories for each item.'
              : 'Choose the equipment available at your gym. Videos will be filtered to only show exercises you can perform with your equipment.'
            }
          </p>
        </div>
      </div>

      {/* Main Card */}
      <div className="equipment-card-container">
        {/* Unified Search Bar - Option C style */}
        <div className="unified-toolbar">
          {/* Search Section */}
          <div className="search-section" ref={dropdownRef}>
            {/* Search Mode Toggle */}
            <div className="search-mode-toggle">
              <button
                className={searchMode === 'name' ? 'active' : ''}
                onClick={() => {
                  setSearchMode('name');
                  setIsDropdownOpen(false);
                  setTimeout(() => inputRef.current?.focus(), 0);
                }}
                title="Search by name"
              >
                <Search size={16} />
              </button>
              <button
                className={searchMode === 'tag' ? 'active' : ''}
                onClick={() => setSearchMode('tag')}
                title="Filter by category"
              >
                <Tag size={16} />
              </button>
            </div>

            {/* Name Search Input */}
            {searchMode === 'name' && (
              <div className="unified-search-input-wrapper">
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search equipment by name..."
                  className="unified-search-input"
                />
                {searchQuery && (
                  <button
                    className="search-clear-btn"
                    onClick={() => setSearchQuery('')}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            )}

            {/* Tag Filter Input */}
            {searchMode === 'tag' && (
              <div 
                className="unified-tag-input"
                onClick={() => {
                  setIsDropdownOpen(true);
                  inputRef.current?.focus();
                }}
              >
                {/* Selected Tag Pills */}
                {selectedTags.map(tag => (
                  <div 
                    key={tag.id}
                    className="selected-tag-pill"
                    style={{ background: tag.color + '20', color: tag.color }}
                  >
                    {tag.name}
                    <button onClick={(e) => { e.stopPropagation(); handleTagRemove(tag.id); }}>
                      <X size={14} />
                    </button>
                  </div>
                ))}

                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setIsDropdownOpen(true)}
                  placeholder={selectedTags.length === 0 ? "Filter by category..." : ""}
                  className="tag-filter-input"
                />

                {selectedTags.length > 0 && (
                  <button
                    className="clear-all-tags"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedTags([]);
                    }}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            )}

            {/* Tag Dropdown */}
            {searchMode === 'tag' && isDropdownOpen && (
              <div className="tag-suggestions-dropdown">
                <div className="dropdown-section-header">Categories</div>
                {filteredCategories.map(cat => {
                  const isSelected = selectedTags.some(t => t.id === cat.id);
                  const equipmentUsingCategory = equipmentList.filter(eq => eq.categoryId === cat.id).length;
                  return (
                    <div
                      key={cat.id}
                      className={`tag-suggestion-item ${isSelected ? 'selected' : ''}`}
                    >
                      <div 
                        className="tag-suggestion-content"
                        onClick={() => {
                          if (isSelected) {
                            handleTagRemove(cat.id);
                          } else {
                            handleTagSelect(cat);
                          }
                          setSearchQuery('');
                        }}
                      >
                        <div className="category-dot" style={{ background: cat.color }} />
                        <span className="category-name">{cat.name}</span>
                        <span className="category-count">({equipmentUsingCategory})</span>
                        {isSelected && <span className="check-mark">✓</span>}
                      </div>
                      {viewMode === 'admin' && (
                        <button
                          className="category-delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCategory(cat.id, cat.name, equipmentUsingCategory);
                          }}
                          title={equipmentUsingCategory > 0 ? `${equipmentUsingCategory} equipment using this category` : 'Delete category'}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  );
                })}
                {filteredCategories.length === 0 && (
                  <div className="dropdown-empty-state">No categories found</div>
                )}
              </div>
            )}
          </div>

          {/* Add Equipment Button (Admin mode only) */}
          {viewMode === 'admin' && (
            <>
              <div className="toolbar-divider" />
              <button className="add-equipment-btn" onClick={handleAddNew}>
                <Plus size={16} />
                <span>Add Equipment</span>
              </button>
            </>
          )}
        </div>

        {/* Summary Row */}
        <div className="equipment-summary-row">
          <div className="summary-left">
            {viewMode === 'user' ? (
              <>
                <span className="selected-count">
                  {Object.keys(selectedEquipment).filter(id => equipmentList.some(eq => eq.id === id)).length} equipment types selected
                </span>
                {hasUnsavedChanges && (
                  <>
                    <span className="summary-dot">•</span>
                    <span className="unsaved-warning">Unsaved changes</span>
                  </>
                )}
              </>
            ) : (
              <span className="selected-count admin-count">
                {equipmentList.length} equipment items in catalog
              </span>
            )}
          </div>
          <span className="showing-count">
            Showing {filteredEquipment.length} items
            {getFilterDescription() && <span className="filter-desc"> • {getFilterDescription()}</span>}
          </span>
        </div>

        {/* Equipment Grid - 3 Columns */}
        <div className="equipment-grid-3col">
          {columns.map((column, colIndex) => (
            <div key={colIndex} className="equipment-column">
              {column.map(item => (
                <EquipmentRow
                  key={item.id}
                  item={item}
                  isSelected={!!selectedEquipment[item.id]}
                  quantity={selectedEquipment[item.id]?.quantity || item.defaultQuantity || 1}
                  isHovered={hoveredItem?.id === item.id}
                  viewMode={viewMode}
                  onToggle={toggleEquipment}
                  onUpdateQty={updateQuantity}
                  onHover={handleHover}
                  onLeave={handleLeave}
                  onEdit={handleEdit}
                />
              ))}
            </div>
          ))}
        </div>

        {filteredEquipment.length === 0 && (
          <div className="equipment-empty">
            No equipment found matching your {searchMode === 'name' ? 'search' : 'filters'}
          </div>
        )}
      </div>
    </div>
  );
}

export default EquipmentProfile;